import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { notEqual, ReactiveElement } from 'lit';

const UNINITIALIZED = Symbol('uninitialized');

const DEVTOOLS_FLAG = 'mdt';

const CACHE_FLAG = 'mdt-cache';

const URL_PARAMS = new URLSearchParams(window.location.search);

export class CachePropertyController<T extends ReactiveControllerHost, P extends keyof T> implements ReactiveController {
  host: T;
  private prevValues = new Map<P, unknown>;

  constructor(host: T, public properties: P[], public key: () => Promise<string | false> | string | false) {
    (this.host = host).addController(this);
  }

  hostUpdate(): void {
    this._check();
  }

  private async _check() {
    // check to make sure we have a key, if not, bail
    const key = await this.key();
    if (!key) {
      return;
    }

    if (this._isCacheDisabled()) {
      return;
    }

    for (const prop of this.properties) {
      // get old and new values
      const oldValue = this.prevValues.get(prop) ?? UNINITIALIZED;
      const newValue = this.host[prop];
      const hasChanged = this._propertyHasChanged(prop, oldValue, newValue);

      // skip if this property has not changed
      if (!hasChanged) {
        continue;
      }

      // define unique key name
      const keyName = `${key}:${String(prop)}`;

      if (!newValue) {
        // read from cache
        const cache = window.sessionStorage.getItem(keyName);
        if (cache) {
          this.host[prop] = JSON.parse(cache);
        }
      } else {
        // write to cache
        window.sessionStorage.setItem(keyName, JSON.stringify(newValue));
      }

      this.prevValues.set(prop, newValue);
    }
  }

  /**
   * Checks if a property has changed
   */
  private _propertyHasChanged(prop: P, oldValue: unknown, newValue: unknown): boolean {
    const Class = (this.host.constructor as typeof ReactiveElement);
    const hasChanged = Class
      .getPropertyOptions(prop)
      .hasChanged ?? notEqual;

    if (hasChanged(oldValue, newValue)) {
      return true;
    }

    return false;
  }

  /**
   * CONDITIONALS
   */
  private _isCacheDisabled(): boolean {
    return URL_PARAMS.get(DEVTOOLS_FLAG) === '1' || URL_PARAMS.get(CACHE_FLAG) === '0';
  }
}

